<template>
  <el-image
    :key="retryKey"
    :class="['w-full h-full relative z-up image-preview-wrapper', className]"
    :src="cachedSrc"
    :fit="fit"
    lazy
    @click="previewImage"
    @load="handleImageLoad"
    @error="handleImageError"
  >
    <template #error>
      <div class="w-full h-full flex items-center justify-center bg-fill-2">
        <Icon
          icon="sky:ic-image-empty-fill"
          class="text-text-4"
          :class="iconSizeClass"
        />
      </div>
    </template>
    <template #placeholder>
      <el-skeleton
        class="w-full h-full relative"
        loading
        animated
      >
        <template #template>
          <el-skeleton-item
            variant="rect"
            class="w-full"
            style="height: 100%;"
          />
          <Icon
            icon="sky:ic-three-star-fill"
            class="text-text-4 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            :class="iconSizeClass"
          />
        </template>
      </el-skeleton>
    </template>
  </el-image>
  <!-- 模糊背景层，仅在图片加载完成后显示 -->
  <img
    v-if="cachedSrc && !noBlur && isImageLoaded"
    :src="cachedSrc"
    alt=""
    class="w-full h-full absolute top-0 left-0 object-cover"
    style="filter: blur(30px);"
    @dragstart.prevent
    @selectstart.prevent
  >
</template>

<script setup lang="ts">
import { useUploader } from '@/modules/common/uploader/use-uploader'
import { computed, onUnmounted, ref, watch } from 'vue'
import { usePreviewImage, type ImagePreviewItem } from '@/hooks/use-preview-image.ts'
import { useCachedResource } from '@/hooks/use-cached-resource'

const RETRY_DELAY_MS = 5000
const MAX_RETRY_COUNT = 5

type Props = {
  src: string
  fit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'
  previewImgList?: ImagePreviewItem[]
  initialIndex?: number
  noBlur?: boolean
  iconSize?: 'small' | 'large' // size目前仅用来设置icon大小
  className?: string
}

const props = withDefaults(defineProps<Props>(), {
  fit: 'contain',
  initialIndex: 0,
  iconSize: 'large',
  className: '',
})

const { ossDataBj } = useUploader()

const baseImgPath = computed(() => {
  return ossDataBj.value?.oss_static_prefix ?? ''
})

const iconSizeClass = computed(() => {
  return props.iconSize === 'small' ? 'size-5' : 'size-14'
})

const originalSrc = computed(() => {
  return props.src.startsWith('http') ? props.src : baseImgPath.value + props.src
})

// 使用缓存的图片（含 error、reload，用于 404 重试）
const { cachedUrl: cachedSrc, error: cacheError, reload: reloadCache } = useCachedResource(originalSrc)

const isImageLoaded = ref(false)
const retryKey = ref(0)
const retryCount = ref(0)
let retryTimer: ReturnType<typeof setTimeout> | null = null

const hasValidAddress = computed(() => props.src.trim() !== '')

const scheduleRetry = () => {
  if (!hasValidAddress.value || retryCount.value >= MAX_RETRY_COUNT) return
  if (retryTimer) clearTimeout(retryTimer)
  retryTimer = setTimeout(() => {
    retryCount.value += 1
    retryKey.value += 1
    reloadCache()
    retryTimer = null
  }, RETRY_DELAY_MS)
}

const handleImageLoad = () => {
  isImageLoaded.value = true
}

// 仅在有地址且请求失败（如 404）时重试，空地址不触发重试
const handleImageError = () => {
  scheduleRetry()
}

// useCachedResource 内请求失败（如 404）时也触发 5 秒后重试
watch(cacheError, err => {
  if (err && hasValidAddress.value) {
    scheduleRetry()
  }
})

// 当图片源变化时，重置加载状态与重试计数
const srcRef = computed(() => props.src)
watch([cachedSrc, srcRef], () => {
  isImageLoaded.value = false
  retryCount.value = 0
  if (retryTimer) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
})

onUnmounted(() => {
  if (retryTimer) clearTimeout(retryTimer)
})

const { showPreview } = usePreviewImage()

const previewImage = () => {
  if (props.previewImgList && props.previewImgList.length > 0) {
    const currentIndex = props.initialIndex ?? 0
    const validIndex = Math.max(0, Math.min(currentIndex, props.previewImgList.length - 1))

    showPreview({
      imageList: props.previewImgList,
      currentIndex: validIndex,
      onClose: () => {
        // 关闭回调
      },
      onNext: () => {
        // 下一张回调
      },
      onPrev: () => {
        // 上一张回调
      },
      onDownload: () => {
        // 下载回调
      },
    })
  }
}
</script>

<style scoped>
.image-preview-wrapper {
  cursor: pointer;
}

.image-preview-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--black-30);
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 1;
  pointer-events: none;
}

.image-preview-wrapper:hover::before {
  opacity: 1;
}
</style>
